import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Typography, Form, Flex, Image, Alert } from "antd";
import CustomButton from "../../components/CustomButton/Button";
import CustomInput from "../../components/CustomInput/Input";
import Logo from "../../assets/images/auth-logo.png";
import {
    loginUser,
    loginUserBeforeOtp,
    loginUserAfterOtp,
    loginUserBeforeAuthenticator,
    loginUserAfterAuthenticator,
    clearAuthError,
} from "../../redux/slices/AuthSlice";
import { useDispatch, useSelector } from "react-redux";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import {
    GLOBAL_CONSTANTS,
    ERROR_MSGS,
    TITLE_TXT,
    BTN_TXT,
} from "../../constants/constants";
import CryptoJS from "crypto-js";
import "./Login.scss";

const authenticatorEnabled =
    process.env.REACT_APP_AUTHENTICATOR_ENABLED === "true";
const otpEnabled =
    !authenticatorEnabled && process.env.REACT_APP_OTP_ENABLED === "true";

const { Title, Text } = Typography;

const INITIAL_AUTH_STEP = {
    mode: "credentials",
    email: "",
    password: "",
    setupToken: null,
    qrCodeDataUrl: null,
    enrolled: false,
};

const Login = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const { loading, error } = useSelector((state) => state.auth);

    const [visible, setVisible] = useState(false);
    const [showOtp, setShowOtp] = useState(false);
    const [authStep, setAuthStep] = useState(INITIAL_AUTH_STEP);
    const [reqId, setReqId] = useState();
    const [email, setEmail] = useState("");
    const [hashedPassword, setHashedPassword] = useState("");
    const [resendTimer, setResendTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const [sessionRevokedNote, setSessionRevokedNote] = useState(null);

    useEffect(() => {
        const notice = sessionStorage.getItem("sessionRevokedMessage");
        if (notice) {
            setSessionRevokedNote(notice);
            sessionStorage.removeItem("sessionRevokedMessage");
        }
    }, []);

    useEffect(() => {
        return () => {
            dispatch(clearAuthError());
        };
    }, [dispatch]);

    const toggleVisibility = () => {
        setVisible(!visible);
    };

    const startResendTimer = () => {
        setCanResend(false);
        setResendTimer(60);
        const interval = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    setCanResend(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const resetAuthFlow = () => {
        dispatch(clearAuthError());
        form.resetFields();
        setShowOtp(false);
        setAuthStep(INITIAL_AUTH_STEP);
        setReqId(undefined);
        setEmail("");
        setHashedPassword("");
        setCanResend(false);
        setResendTimer(60);
    };

    const hashPassword = (password) => CryptoJS.SHA256(password).toString();

    const handleAuthenticatorStep = async (values) => {
        if (authStep.mode === "credentials") {
            const password = hashPassword(values.password);
            const res = await dispatch(
                loginUserBeforeAuthenticator({
                    email: values.email,
                    password,
                })
            );

            if (res.meta.requestStatus !== "fulfilled") {
                return;
            }

            const response = res.payload || {};
            form.resetFields(["totp"]);

            if (response.enrolled) {
                setAuthStep({
                    mode: "verify",
                    email: values.email,
                    password,
                    setupToken: null,
                    qrCodeDataUrl: null,
                    enrolled: true,
                });
                return;
            }

            setAuthStep({
                mode: "enroll",
                email: values.email,
                password,
                setupToken: response.setupToken,
                qrCodeDataUrl: response.qrCodeDataUrl,
                enrolled: false,
            });
            return;
        }

        const payload = {
            email: authStep.email,
            password: authStep.password,
            totp: values.totp,
        };

        if (authStep.mode === "enroll" && authStep.setupToken) {
            payload.setupToken = authStep.setupToken;
        }

        const res = await dispatch(loginUserAfterAuthenticator(payload));

        if (res.payload?.userId) {
            resetAuthFlow();
            navigate("/kyc");
        }
    };

    const handleSubmit = async (values) => {
        if (authenticatorEnabled) {
            await handleAuthenticatorStep(values);
            return;
        }

        const hashed = hashPassword(values.password);

        if (!otpEnabled) {
            const res = await dispatch(
                loginUser({
                    email: values.email,
                    password: hashed,
                })
            );

            if (res.payload?.userId) {
                form.resetFields();
                navigate("/kyc");
            }
            return;
        }

        const res = await dispatch(
            loginUserBeforeOtp({
                email: values.email,
                password: hashed,
            })
        );

        if (res.meta.requestStatus === "fulfilled") {
            setEmail(values.email);
            setReqId(res.payload.reqId);
            setHashedPassword(hashed);
            setShowOtp(true);
            startResendTimer();
        }
    };

    const handleOtpSubmit = async (values) => {
        const res = await dispatch(
            loginUserAfterOtp({
                email,
                password: hashedPassword,
                otp: values.otp,
                reqId,
            })
        );

        if (res.payload?.userId) {
            form.resetFields();
            navigate("/kyc");
        }
    };

    const handleResendOtp = async () => {
        if (!canResend) return;

        const res = await dispatch(
            loginUserBeforeOtp({
                email,
                password: hashedPassword,
            })
        );

        if (res.meta.requestStatus === "fulfilled") {
            setReqId(res.payload.reqId);
            startResendTimer();
        }
    };

    const showAuthenticatorStep =
        authenticatorEnabled && authStep.mode !== "credentials";
    const showEmailOtpStep = otpEnabled && showOtp;
    const submitLabel = showAuthenticatorStep
        ? BTN_TXT.SUBMIT
        : showEmailOtpStep
          ? "Verify OTP"
          : BTN_TXT.LOGIN;

    return (
        <div className="auth-container">
            <div className="logo">
                <Image preview={false} src={Logo} alt="Logo" />
            </div>

            <Title className="hello-text">{TITLE_TXT.WELCOME_TEXT}</Title>

            <Form
                form={form}
                layout="vertical"
                className="login-form"
                onFinish={
                    showAuthenticatorStep
                        ? handleAuthenticatorStep
                        : showEmailOtpStep
                          ? handleOtpSubmit
                          : handleSubmit
                }
                initialValues={{ remember: true }}
            >
                {showAuthenticatorStep ? (
                    <>
                        {authStep.mode === "enroll" && authStep.qrCodeDataUrl && (
                            <div className="authenticator-panel">
                                <Text strong className="authenticator-title">
                                    Set up Microsoft Authenticator
                                </Text>
                                <Text className="authenticator-hint">
                                    Scan this QR code in Microsoft Authenticator, then
                                    enter the 6-digit code below.
                                </Text>
                                <Image
                                    preview={false}
                                    src={authStep.qrCodeDataUrl}
                                    alt="Microsoft Authenticator QR code"
                                    width={168}
                                    className="authenticator-qr"
                                />
                            </div>
                        )}

                        {authStep.mode === "verify" && (
                            <div className="authenticator-verify">
                                <Text className="authenticator-verify-text">
                                    Enter the 6-digit code from Microsoft Authenticator
                                    for {authStep.email}.
                                </Text>
                                <Text className="authenticator-verify-help">
                                    Lost authenticator? Contact support.
                                </Text>
                            </div>
                        )}

                        <CustomInput
                            label="Authenticator Code"
                            name="totp"
                            type="text"
                            placeholder="Enter 6-digit code"
                            rules={[
                                {
                                    required: true,
                                    message: "Please enter the authenticator code",
                                },
                                {
                                    pattern: /^\d{6}$/,
                                    message: "Enter a valid 6-digit code",
                                },
                            ]}
                        />
                    </>
                ) : showEmailOtpStep ? (
                    <>
                        <CustomInput
                            label="OTP"
                            name="otp"
                            type="text"
                            placeholder="Enter the 6-digit OTP"
                            maxLength={6}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                form.setFieldsValue({ otp: value });
                            }}
                            rules={[
                                { required: true, message: "Please enter the OTP" },
                                {
                                    pattern: /^\d{6}$/,
                                    message: "OTP must be exactly 6 digits",
                                },
                            ]}
                        />
                        <div style={{ textAlign: "right", marginBottom: 20 }}>
                            <Text
                                type={canResend ? "primary" : "secondary"}
                                onClick={handleResendOtp}
                                style={{ cursor: canResend ? "pointer" : "not-allowed" }}
                            >
                                {canResend ? "Resend OTP" : `Resend in ${resendTimer}s`}
                            </Text>
                        </div>
                    </>
                ) : (
                    <>
                        <CustomInput
                            label="Email"
                            name="email"
                            type="email"
                            placeholder="Your Email"
                            rules={[
                                {
                                    required: true,
                                    message: ERROR_MSGS.EMAIL_ERR_MSG,
                                },
                            ]}
                        />

                        <CustomInput
                            label="Password"
                            name="password"
                            type={visible ? "text" : "password"}
                            isPassword={true}
                            iconRender={(visible) =>
                                visible ? (
                                    <EyeInvisibleOutlined onClick={toggleVisibility} />
                                ) : (
                                    <EyeOutlined onClick={toggleVisibility} />
                                )
                            }
                            placeholder="Password"
                            rules={[
                                {
                                    required: true,
                                    message: ERROR_MSGS.PWD_ERR_MSG,
                                },
                            ]}
                        />

                        <Flex justify="flex-end" style={{ marginBottom: 20 }}>
                            <Text
                                onClick={() => navigate("/forgot-password")}
                                className="forgot-password-link"
                            >
                                {GLOBAL_CONSTANTS.FORGOT_PWD}
                            </Text>
                        </Flex>
                    </>
                )}

                {sessionRevokedNote && (
                    <Alert
                        style={{ marginBottom: 20 }}
                        message={sessionRevokedNote}
                        type="warning"
                        showIcon
                        closable
                        onClose={() => setSessionRevokedNote(null)}
                    />
                )}

                {error && (
                    <Alert
                        style={{ marginBottom: 20 }}
                        message={error}
                        type="error"
                        showIcon
                    />
                )}

                <Form.Item>
                    <CustomButton
                        loading={loading}
                        type="primary"
                        htmlType="submit"
                        block
                    >
                        {submitLabel}
                    </CustomButton>
                </Form.Item>

                {(showAuthenticatorStep || showEmailOtpStep) && (
                    <CustomButton type="text" block onClick={resetAuthFlow}>
                        {`${BTN_TXT.BACK} To Login`}
                    </CustomButton>
                )}
            </Form>

            {!showAuthenticatorStep && !showEmailOtpStep && (
                <Text className="already-user">
                    {GLOBAL_CONSTANTS.NEW_USER}{" "}
                    <Link to="/enroll-as">
                        <span>{BTN_TXT.SIGNUP}</span>
                    </Link>
                </Text>
            )}
        </div>
    );
};

export default Login;
